import { setAuthToken, setStoredUser } from '@/utils/auth';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, AlertCircle, Clock, Mail, Lock, CheckCircle, Search, ArrowRight, KeyRound } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Logo } from '@/components/Logo';
import { RegionSelector } from '@/components/RegionSelector';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Detect the portal-mismatch payload from a 403 response (mirrors LoginPage logic).
const detectPortalMismatch = (detail) => {
  if (!detail || typeof detail !== 'string') return null;
  const d = detail.toLowerCase();
  if (!d.includes('belongs to') || !d.includes('portal')) return null;
  if (d.includes('india')) return { region: 'IN', label: 'India', path: '/india/partner/login' };
  if (d.includes('dubai') || d.includes('uae')) return { region: 'UAE', label: 'Dubai (UAE)', path: '/uae/partner/login' };
  return null;
};

const PartnerLoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mismatch, setMismatch] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  
  // Application status check
  const [showStatusCheck, setShowStatusCheck] = useState(false);
  const [statusEmail, setStatusEmail] = useState('');
  const [statusLoading, setStatusLoading] = useState(false);
  const [applicationStatus, setApplicationStatus] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setMismatch(null);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/partner/login`, formData);
      
      setAuthToken(res.data.token);
      setStoredUser(res.data.user);
      axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
      
      toast.success('Welcome back!');
      
      // Redirect based on role
      const role = res.data.user.role;
      const dashboardRoutes = {
        'developer': '/partner/dashboard/developer',
        'broker': '/partner/dashboard/broker',
        'agent': '/partner/dashboard/agent',
        'car_dealer': '/partner/dashboard/cars',
        'yacht_dealer': '/partner/dashboard/yachts',
        'interior_designer': '/partner/dashboard/design',
        'other_business': '/partner/dashboard/services'
      };
      
      navigate(dashboardRoutes[role] || '/partner/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail;
      const status = error.response?.status;

      // Cross-region mismatch: render a friendly portal-switch banner instead of
      // a generic "Invalid credentials" toast.
      if (status === 403) {
        const m = detectPortalMismatch(detail);
        if (m) {
          setMismatch(m);
          setError(null);
          return;
        }
      }

      setError(detail || 'Login failed. Please try again.');

      // Show specific toast based on error type
      if (detail?.includes('under review')) {
        toast.info('Your application is being reviewed');
      } else if (detail?.includes('complete your account')) {
        toast.info('Please check your email to complete account setup');
      } else if (detail?.includes('not approved')) {
        toast.error('Application not approved');
      } else if (detail) {
        // Show the actual server message instead of a misleading "Invalid credentials" toast.
        toast.error(detail);
      } else {
        toast.error('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Switch portals via full-page navigation. The interceptor reads the URL prefix
  // (/in/* or /uae/*) and sends the correct X-Portal-Region. We persist the email
  // in sessionStorage so the destination page can prefill it (same UX as customer login).
  const handleSwitchPortal = () => {
    if (!mismatch) return;
    try {
      sessionStorage.setItem('dreamlife_partner_switch_email', formData.email);
      sessionStorage.setItem('dreamlife_partner_switched_to', mismatch.label);
    } catch { /* ignore */ }
    window.location.assign(mismatch.path);
  };

  // After a portal switch redirect, prefill the email field. Also auto-route
  // returning partners (with a saved country preference) past the selector.
  const isOnInPortal = location.pathname.startsWith('/in/') || location.pathname.startsWith('/india/');
  const isOnUaePortal = location.pathname.startsWith('/uae/');

  useEffect(() => {
    // ASTIIV BASGAT product invariant (2026-02-27): region selection is
    // ALWAYS explicit. Do NOT auto-redirect returning visitors based on
    // localStorage, cookies, IP, or device history — the selector must
    // re-appear on every visit. (Previous behaviour: remembered the last
    // picked region — removed deliberately.)
    try {
      const stashedEmail = sessionStorage.getItem('dreamlife_partner_switch_email');
      const switchedTo = sessionStorage.getItem('dreamlife_partner_switched_to');
      if (stashedEmail) {
        setFormData(p => ({ ...p, email: stashedEmail }));
        sessionStorage.removeItem('dreamlife_partner_switch_email');
      }
      if (switchedTo) {
        toast.success(`Switched to ${switchedTo} portal — enter your password to continue.`);
        sessionStorage.removeItem('dreamlife_partner_switched_to');
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getErrorIcon = () => {
    if (!error) return null;
    if (error.includes('under review')) return <Clock className="w-5 h-5" />;
    if (error.includes('complete your account')) return <Mail className="w-5 h-5" />;
    return <AlertCircle className="w-5 h-5" />;
  };

  const getErrorColor = () => {
    if (!error) return '';
    if (error.includes('under review')) return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    if (error.includes('complete your account')) return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    return 'bg-red-500/20 text-red-400 border-red-500/30';
  };

  // Check application status
  const handleCheckStatus = async (e) => {
    e.preventDefault();
    setStatusLoading(true);
    setApplicationStatus(null);
    
    try {
      const res = await axios.get(`${API}/partner/application-status/${encodeURIComponent(statusEmail)}`);
      setApplicationStatus(res.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setApplicationStatus({ error: 'No application found with this email address.' });
      } else {
        setApplicationStatus({ error: 'Failed to check status. Please try again.' });
      }
    } finally {
      setStatusLoading(false);
    }
  };

  // Render application status result
  const renderStatusResult = () => {
    if (!applicationStatus) return null;
    
    if (applicationStatus.error) {
      return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-4 p-4 rounded-lg bg-red-500/20 text-red-400 border border-red-500/30">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            <span>{applicationStatus.error}</span>
          </div>
        </motion.div>
      );
    }
    
    const statusConfig = {
      pending: {
        icon: <Clock className="w-6 h-6" />,
        color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
        title: 'Application Under Review',
        message: 'Your application is being reviewed by our team. We\'ll notify you once a decision is made.'
      },
      approved: {
        icon: <CheckCircle className="w-6 h-6" />,
        color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
        title: 'Application Approved!',
        message: applicationStatus.account_created 
          ? 'Your account is ready. Please login with your credentials.'
          : 'Please create your password to complete account setup.'
      },
      rejected: {
        icon: <AlertCircle className="w-6 h-6" />,
        color: 'bg-red-500/20 text-red-400 border-red-500/30',
        title: 'Application Not Approved',
        message: 'Unfortunately, your application was not approved at this time.'
      }
    };
    
    const config = statusConfig[applicationStatus.status] || statusConfig.pending;
    
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={`mt-4 p-6 rounded-lg border ${config.color}`}>
        <div className="flex items-start gap-4">
          {config.icon}
          <div className="flex-1">
            <h4 className="font-semibold text-lg mb-1">{config.title}</h4>
            <p className="text-sm opacity-80 mb-3">{config.message}</p>
            
            {/* Show setup button for approved but not yet setup accounts */}
            {applicationStatus.status === 'approved' && !applicationStatus.account_created && applicationStatus.approval_token && (
              <Button 
                onClick={() => navigate(`/partner/setup?token=${applicationStatus.approval_token}`)}
                className="bg-emerald-500 text-white hover:bg-emerald-600 mt-2"
                data-testid="setup-account-btn"
              >
                <KeyRound className="w-4 h-4 mr-2" /> Create Password & Login
              </Button>
            )}
            
            {/* Show login prompt for accounts already set up */}
            {applicationStatus.status === 'approved' && applicationStatus.account_created && (
              <Button 
                onClick={() => {
                  setShowStatusCheck(false);
                  setFormData({ ...formData, email: statusEmail });
                }}
                className="bg-gold text-black hover:bg-gold-light mt-2"
              >
                Go to Login <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
        
        {/* Company details */}
        <div className="mt-4 pt-4 border-t border-white/10 text-sm opacity-70">
          <p><strong>Company:</strong> {applicationStatus.company_name}</p>
          <p><strong>Applied:</strong> {new Date(applicationStatus.created_at).toLocaleDateString()}</p>
        </div>
      </motion.div>
    );
  };

  // When the URL has no regional prefix, gate this entry with the
  // canonical RegionSelector. NO memory / no localStorage write.
  if (!isOnInPortal && !isOnUaePortal) {
    return (
      <RegionSelector
        title="Partner Portal"
        subtitle="Select your region to continue"
        buildHref={(code) => (code === 'india' ? '/india/partner/login' : '/uae/partner/login')}
        bottomNote={
          <>New to ASTIIV BASGAT? <Link to="/partner" className="text-gold hover:underline">Apply to become a partner</Link></>
        }
        testIdPrefix="partner-country"
      />
    );
  }

  return (
    <div className="min-h-screen bg-obsidian text-text-primary flex items-center justify-center p-4">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gold/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gold/5 rounded-full blur-3xl" />
      </div>

      {/* Regional login form (only reached after explicit selection above) */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="glass-card p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center">
              <Logo to="/" size="md" wordmarkClassName="text-gold" testId="partner-login-logo" />
            </div>
            <h2 className="text-xl text-text-secondary mt-2">Partner Portal{isOnInPortal ? ' · India' : isOnUaePortal ? ' · Dubai (UAE)' : ''}</h2>
          </div>

          {/* Portal Mismatch Banner — friendly switch-portal CTA (parallels customer login UX) */}
          <AnimatePresence>
            {mismatch && (
              <motion.div
                key="partner-portal-mismatch"
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                className="mb-6 rounded-md border border-gold/40 bg-gold/5 p-4"
                data-testid="partner-portal-mismatch-banner"
              >
                <p className="text-sm leading-relaxed">
                  This account is registered on the <span className="text-gold font-semibold">{mismatch.label}</span> partner portal.
                  Switch portals to continue with the same credentials.
                </p>
                <div className="mt-4 flex flex-col sm:flex-row gap-3">
                  <Button type="button" onClick={handleSwitchPortal}
                    className="flex-1 bg-gold text-black hover:bg-gold-light uppercase tracking-widest text-xs font-bold py-3"
                    data-testid="partner-switch-portal-btn">
                    Switch to {mismatch.label} Portal
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setMismatch(null)}
                    className="flex-1 border border-white/15 text-text-secondary hover:text-text-primary hover:bg-white/5 uppercase tracking-widest text-xs py-3"
                    data-testid="partner-dismiss-mismatch-btn">
                    Use a different account
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Error Message */}
          {error && !mismatch && !showStatusCheck && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex items-center gap-3 p-4 rounded-lg border mb-6 ${getErrorColor()}`}
            >
              {getErrorIcon()}
              <div className="flex-1">
                <p className="text-sm">{error}</p>
                {(error.includes('complete your account') || error.includes('Invalid')) && (
                  <button 
                    type="button"
                    onClick={() => {
                      setShowStatusCheck(true);
                      setStatusEmail(formData.email);
                    }}
                    className="text-sm underline mt-1 hover:no-underline"
                  >
                    Check application status
                  </button>
                )}
              </div>
            </motion.div>
          )}

          {/* Toggle between Login and Status Check */}
          {showStatusCheck ? (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <Search className="w-5 h-5 text-gold" /> Check Application Status
                </h3>
                <button 
                  onClick={() => {
                    setShowStatusCheck(false);
                    setApplicationStatus(null);
                  }}
                  className="text-sm text-gold hover:underline"
                >
                  Back to Login
                </button>
              </div>
              
              <form onSubmit={handleCheckStatus} className="space-y-4">
                <div>
                  <Label className="text-text-primary">Email Address</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="email"
                      value={statusEmail}
                      onChange={(e) => setStatusEmail(e.target.value)}
                      className="bg-white text-black pl-10"
                      placeholder="Enter your application email"
                      required
                      data-testid="status-email-input"
                    />
                  </div>
                </div>
                
                <Button
                  type="submit"
                  disabled={statusLoading}
                  className="w-full bg-gold text-black hover:bg-gold-light py-5"
                  data-testid="check-status-btn"
                >
                  {statusLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Check Status'}
                </Button>
              </form>
              
              {renderStatusResult()}
            </motion.div>
          ) : (
            /* Login Form */
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <Label className="text-text-primary">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-white text-black pl-10"
                    placeholder="partner@company.com"
                    required
                    data-testid="partner-email-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-text-primary">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="bg-white text-black pl-10"
                    placeholder="••••••••"
                    required
                    data-testid="partner-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gold text-black hover:bg-gold-light py-6"
                data-testid="partner-login-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Sign In'}
              </Button>
            </form>
          )}

          {/* Links */}
          <div className="mt-6 text-center space-y-3">
            {!showStatusCheck && (
              <p className="text-text-secondary text-sm">
                Applied already?{' '}
                <button 
                  type="button"
                  onClick={() => setShowStatusCheck(true)} 
                  className="text-gold hover:underline"
                  data-testid="check-status-link"
                >
                  Check application status
                </button>
              </p>
            )}
            <p className="text-text-secondary text-sm">
              Not a partner yet?{' '}
              <Link to="/partner/apply" className="text-gold hover:underline">
                Apply for Partnership
              </Link>
            </p>
            <p className="text-text-secondary text-[11px] mt-3 leading-relaxed">
              By signing in you agree to our{' '}
              <Link to="/legal/terms" className="text-gold hover:underline" data-testid="partner-tnc-link">
                Terms & Conditions
              </Link>{' '}and{' '}
              <Link to="/legal/partner-agreement" className="text-gold hover:underline" data-testid="partner-agreement-link">
                Partner Agreement
              </Link>.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default PartnerLoginPage;
